import React, { createContext, useContext, ReactNode } from 'react';
import { useToast as useToastHook } from '../hooks/useToast';
import ToastContainer from '../components/common/ToastContainer';

interface ToastContextType {
  success: (title: string, message?: string, options?: { duration?: number }) => string;
  error: (title: string, message?: string, options?: { 
    duration?: number;
    action?: { label: string; onClick: () => void };
  }) => string;
  warning: (title: string, message?: string, options?: { duration?: number }) => string;
  info: (title: string, message?: string, options?: { duration?: number }) => string;
  clearToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const toastHook = useToastHook();

  return (
    <ToastContext.Provider value={toastHook}>
      {children}
      <ToastContainer 
        toasts={toastHook.toasts}
        onCloseToast={toastHook.removeToast}
        position="top-right"
      />
    </ToastContext.Provider>
  );
};

export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
};

// 为了保持一致性，也导出useToast别名
export const useToast = useToastContext;

// 全局错误处理函数
export const handleApiError = (error: any, toast: ToastContextType) => {
  console.error('API Error:', error);
  
  if (error?.message) {
    // 根据错误类型显示不同的Toast
    if (error.message.includes('网络')) {
      toast.error('网络错误', '请检查网络连接后重试');
    } else if (error.message.includes('验证码')) {
      toast.error('验证失败', error.message);
    } else if (error.message.includes('已注册')) {
      toast.warning('用户已存在', error.message);
    } else if (error.message.includes('未注册')) {
      toast.error('用户不存在', error.message);
    } else {
      toast.error('操作失败', error.message);
    }
  } else {
    toast.error('系统错误', '发生未知错误，请稍后重试');
  }
};

export default ToastContext;